import React, {
  useEffect,
  useContext,
  useRef,
  useState,
  useMemo,
  useCallback,
} from "react";
import { useDispatch } from "react-redux";
import { userSlice } from "@/redux/User";
import { renderHTML, isHtml } from "@/utils/renderUtility";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeExternalLinks from "rehype-external-links";
import { PrismLight as SyntaxHighlighter } from "react-syntax-highlighter";
import { BotContext } from "../store";
import {
  vscDarkPlus as themeDark,
  vs as themeLight,
} from "react-syntax-highlighter/dist/esm/styles/prism";
import rehypeRaw from "rehype-raw";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
interface ViewDetailsModalProps {
  onClose: () => void;
  url: string;
  content: string[];
  isLoading?: boolean;
}

const ViewDetailsModal = ({
  onClose,
  url,
  content,
  isLoading = false,
}: ViewDetailsModalProps) => {
  // console.log("Content in ViewDetailsModal:", content);
  const boxRef = useRef<any>(null);
  const [searchInput, setSearchInput] = useState<string>("");
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
  const [totalMatches, setTotalMatches] = useState(0);
  const [store] = useContext(BotContext);
  const dispatch = useDispatch();
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [contentLoaded, setContentLoaded] = useState(false);
  const chatConfig = store.botData.chatConfig;
  const typography = chatConfig?.typography;
  const [displayHeading, setDisplayHeading] = useState(url);

  useEffect(() => {
    if (url) {
      setDisplayHeading(url);
    }
  }, [url]);

  // Clear highlights and perform new search
  const performSearch = useCallback((searchTerm: string) => {
    if (!contentRef.current) return;

    // Clear previous highlights
    const existingHighlights =
      contentRef.current.querySelectorAll(".search-highlight");
    existingHighlights.forEach((highlight) => {
      const parent = highlight.parentNode;
      if (parent) {
        parent.replaceChild(
          document.createTextNode(highlight.textContent || ""),
          highlight,
        );
        parent.normalize();
      }
    });

    if (!searchTerm.trim()) {
      setTotalMatches(0);
      setCurrentMatchIndex(0);
      return;
    }

    // Find and highlight all matches
    const walker = document.createTreeWalker(
      contentRef.current,
      NodeFilter.SHOW_TEXT,
      null,
    );

    const textNodes: Text[] = [];
    let node;
    while ((node = walker.nextNode())) {
      textNodes.push(node as Text);
    }

    const matches: { node: Text; index: number; length: number }[] = [];
    const searchTermLower = searchTerm.toLowerCase();

    textNodes.forEach((textNode) => {
      const text = textNode.textContent || "";
      const textLower = text.toLowerCase();
      let index = 0;

      while (index < text.length) {
        const foundIndex = textLower.indexOf(searchTermLower, index);
        if (foundIndex === -1) break;

        matches.push({
          node: textNode,
          index: foundIndex,
          length: searchTerm.length,
        });

        index = foundIndex + 1;
      }
    });

    // Apply highlights in reverse order to maintain text node positions
    matches.reverse().forEach((match, i) => {
      const { node, index, length } = match;
      const text = node.textContent || "";

      // Split the text node
      const beforeText = text.substring(0, index);
      const matchText = text.substring(index, index + length);
      const afterText = text.substring(index + length);

      // Create highlight element
      const highlight = document.createElement("span");
      highlight.className = "search-highlight";
      highlight.style.backgroundColor = "#FFFF00";
      highlight.style.color = "#000000bd";
      highlight.style.padding = "0 2px";
      highlight.textContent = matchText;

      // Replace the text node with new structure
      const parent = node.parentNode;
      if (parent) {
        if (beforeText) {
          parent.insertBefore(document.createTextNode(beforeText), node);
        }
        parent.insertBefore(highlight, node);
        if (afterText) {
          parent.insertBefore(document.createTextNode(afterText), node);
        }
        parent.removeChild(node);
      }
    });

    setTotalMatches(matches.length);
    setCurrentMatchIndex(matches.length > 0 ? 1 : 0);

    // Scroll to first match
    if (matches.length > 0) {
      const firstHighlight =
        contentRef.current.querySelector(".search-highlight");
      if (firstHighlight) {
        firstHighlight.scrollIntoView({ behavior: "smooth", block: "center" });
        firstHighlight.classList.add("current-match");
      }
    }
  }, []);

  // Navigate between matches
  const navigateToMatch = useCallback(
    (direction: "next" | "prev") => {
      if (!contentRef.current || totalMatches === 0) return;

      const highlights =
        contentRef.current.querySelectorAll(".search-highlight");
      const currentHighlight =
        contentRef.current.querySelector(".current-match");

      let newIndex = currentMatchIndex;

      if (direction === "next") {
        newIndex =
          currentMatchIndex >= totalMatches ? 1 : currentMatchIndex + 1;
      } else {
        newIndex =
          currentMatchIndex <= 1 ? totalMatches : currentMatchIndex - 1;
      }

      // Remove current match highlighting
      if (currentHighlight) {
        currentHighlight.classList.remove("current-match");
      }

      // Add highlighting to new match
      const newHighlight = highlights[newIndex - 1];
      if (newHighlight) {
        newHighlight.classList.add("current-match");
        newHighlight.scrollIntoView({ behavior: "smooth", block: "center" });
      }

      setCurrentMatchIndex(newIndex);
    },
    [currentMatchIndex, totalMatches],
  );

  // Handle search input changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      performSearch(searchInput);
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [searchInput, performSearch]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Enter" && searchInput.trim()) {
        e.preventDefault();
        navigateToMatch(e.shiftKey ? "prev" : "next");
      } else if (e.key === "Escape") {
        setSearchInput("");
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [searchInput, navigateToMatch]);

  useEffect(() => {
    if (!isLoading && content.length > 0) {
      const timer = setTimeout(() => {
        setContentLoaded(true);
      }, 100);
      return () => clearTimeout(timer);
    } else {
      setContentLoaded(false);
    }
  }, [isLoading, content]);

  const handleCopy = async () => {
    const textToCopy = content.join("\n\n");
    try {
      await navigator.clipboard.writeText(textToCopy);
      dispatch(
        userSlice.actions.setToastPopUp({
          show: true,
          toastType: "success",
          message: "The extracted data has been copied!",
        }),
      );
    } catch (err) {
      console.log(err);
    }
  };

  const preprocessMarkdown = (text: string): string => {
    const lines = text.split("\n");
    const processedLines = [...lines];
    for (let i = 0; i < lines.length; i++) {
      const currentLine = lines[i].trim();
      if (currentLine.startsWith("|") && currentLine.endsWith("|")) {
        const prevLine = lines[i - 1]?.trim() || "";
        const nextLine = lines[i + 1]?.trim() || "";
        const hasTableSeparator =
          nextLine.match(/^\|[\s-:|]+\|$/) || prevLine.match(/^\|[\s-:|]+\|$/);
        const hasTableHeaders =
          (prevLine.includes("|") && nextLine.match(/^\|[\s-:|]+\|$/)) ||
          (nextLine.includes("|") && prevLine.match(/^\|[\s-:|]+\|$/));
        if (!hasTableSeparator && !hasTableHeaders) {
          const isPrevTableLike =
            prevLine.startsWith("|") && prevLine.endsWith("|");
          const isNextTableLike =
            nextLine.startsWith("|") && nextLine.endsWith("|");
          if (!isPrevTableLike && !isNextTableLike) {
            const cells = currentLine.split("|").filter((cell) => cell.trim());
            if (cells.length === 1) {
              processedLines[i] =
                `| Value |\n|-------|\n| ${cells[0].trim()} |`;
            }
            //  else if (cells.length > 1) {
            //   const headers = cells.map(() => 'Column').map((h, idx) => `${h} ${idx + 1}`);
            //   const separator = cells.map(() => '-------');
            //   processedLines[i] = `| ${headers.join(' | ')} |\n| ${separator.join(' | ')} |\n| ${cells.map(c => c.trim()).join(' | ')} |`;
            // }
          } else {
            if (!isPrevTableLike && isNextTableLike) {
              const tableLines = [currentLine];
              let j = i + 1;
              while (
                j < lines.length &&
                lines[j].trim().startsWith("|") &&
                lines[j].trim().endsWith("|")
              ) {
                tableLines.push(lines[j].trim());
                j++;
              }
              if (tableLines.length > 1) {
                const maxCells = Math.max(
                  ...tableLines.map(
                    (line) =>
                      line.split("|").filter((cell) => cell.trim()).length,
                  ),
                );

                const headers = Array.from(
                  { length: maxCells },
                  (_, idx) => ``,
                );
                const separator = Array.from(
                  { length: maxCells },
                  () => "-------",
                );

                let tableMarkdown = `| ${headers.join(" | ")} |\n| ${separator.join(" | ")} |\n`;

                tableLines.forEach((line) => {
                  const cells = line.split("|").filter((cell) => cell.trim());
                  while (cells.length < maxCells) cells.push(""); // Pad with empty cells
                  tableMarkdown += `| ${cells.map((c) => c.trim()).join(" | ")} |\n`;
                });

                processedLines[i] = tableMarkdown.trim();
                for (let k = i + 1; k < j; k++) {
                  processedLines[k] = "";
                }
              }
            }
          }
        }
      }
    }
    return processedLines.filter((line) => line !== "").join("\n");
  };

  const formatLineBreaks = (text: string): string => {
    let processed = text.replace(/\\n/g, "\n").replace(/"/g, "");

    processed = processed.replace(/\r\n?/g, "\n");

    processed = processed.replace(/[\x00-\x08\x0B\x0C\x0D\x0E-\x1F\x7F]/g, "");

    processed = processed.replace(/\n{3,}/g, "\n\n");

    processed = processed.replace(/\n\n/g, "\n\n&nbsp;\n\n");

    return preprocessMarkdown(processed);
  };

  const formatLineBreaksAlternative = (text: string): string => {
    let processed = text
      .replace(/\\n/g, "\n") // Convert escaped newlines
      .replace(/"/g, "") // Remove quotes
      .replace(/\r\n/g, "\n") // Normalize Windows line endings
      .replace(/\r/g, "\n"); // Normalize old Mac line endings

    const paragraphs = processed.split(/\n\s*\n/);
    processed = paragraphs
      .map((para) => para.trim())
      .filter((para) => para.length > 0) // Remove empty paragraphs
      .join("\n\n");

    return preprocessMarkdown(processed);
  };

  const formatLineBreaksSimple = (text: string): string => {
    let processed = text.replace(/\\n/g, "\n").replace(/"/g, "");

    processed = processed.replace(/\n{2,}/g, "\n\n");

    processed = processed.replace(/(?<!\n)\n(?!\n)/g, "  \n");

    return preprocessMarkdown(processed);
  };

  const isSpreadsheetJson = (text: string): boolean =>
    typeof text === "string" && text.includes("===SHEET:");

  const formatSpreadsheetJson = (text: string): string => {
    const sections = text.split(/(?=^===SHEET:)/m);
    return sections
      .map((section) => {
        const match = section.match(/^===SHEET:(.*?)===\s*([\s\S]*)$/);
        if (!match) return section.trim();

        const sheetName = match[1].trim();
        const body = match[2].trim();

        let prettyBody = body;
        try {
          prettyBody = JSON.stringify(JSON.parse(body), null, 2);
          prettyBody = prettyBody.replace(/\\n/g, "\n").replace(/\\t/g, "\t");
        } catch {
          // Leave the body as-is if it isn't valid JSON.
        }

        return `===SHEET:${sheetName}===\n${prettyBody}`;
      })
      .join("\n\n");
  };

  // Memoize the rendered content - render everything once
  const renderedContent = useMemo(() => {
    return content.map((item, index) => {
      if (isSpreadsheetJson(item)) {
        return (
          <pre
            key={index}
            className="viewDetails-json"
            style={{
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              fontFamily:
                "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
              fontSize: "12px",
              lineHeight: "1.5",
              color: "#1a1a1a",
              margin: 0,
            }}
          >
            {formatSpreadsheetJson(item)}
          </pre>
        );
      }

      return (
        <Markdown
          key={index}
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[
            [rehypeRaw, { passThrough: ["element"] }],
            [
              rehypeExternalLinks,
              {
                target: "_blank",
                properties: { class: "response-link" },
              },
            ],
          ]}
          components={{
            // Headers with better hierarchy and spacing
            h1(props: any) {
              const { children, ...rest } = props;
              return (
                <h1
                  {...rest}
                  style={{
                    fontFamily: "Rubik Regular",
                    fontSize: "24px", // H1 = 24px
                    color: "#1a1a1a",
                    fontWeight: "700",
                    marginTop: "16px",
                    marginBottom: "8px",
                    lineHeight: "1.2",
                    borderBottom: "2px solid #e1e5e9",
                    paddingBottom: "6px",
                  }}
                >
                  {children}
                </h1>
              );
            },
            h2(props: any) {
              const { children, ...rest } = props;
              return (
                <h2
                  {...rest}
                  style={{
                    fontFamily: "Rubik Regular",
                    fontSize: "20px", // H2 = 20px (24-4)
                    color: "#2d3748",
                    fontWeight: "600",
                    marginTop: "14px",
                    marginBottom: "6px",
                    lineHeight: "1.3",
                    borderBottom: "1px solid #e2e8f0",
                    paddingBottom: "4px",
                  }}
                >
                  {children}
                </h2>
              );
            },
            h3(props: any) {
              const { children, ...rest } = props;
              return (
                <h3
                  {...rest}
                  style={{
                    fontFamily: "Rubik Regular",
                    fontSize: "16px", // H3 = 16px (20-4)
                    color: "#374151",
                    fontWeight: "600",
                    marginTop: "12px",
                    marginBottom: "6px",
                    lineHeight: "1.4",
                  }}
                >
                  {children}
                </h3>
              );
            },
            h4(props: any) {
              const { children, ...rest } = props;
              return (
                <h4
                  {...rest}
                  style={{
                    fontFamily: "Rubik Regular",
                    fontSize: "12px", // H4 = 12px (16-4)
                    color: "#4b5563",
                    fontWeight: "600",
                    marginTop: "10px",
                    marginBottom: "4px",
                    lineHeight: "1.4",
                  }}
                >
                  {children}
                </h4>
              );
            },
            h5(props: any) {
              const { children, ...rest } = props;
              return (
                <h5
                  {...rest}
                  style={{
                    fontFamily: "Rubik Regular",
                    fontSize: "8px", // H5 = 8px (12-4)
                    color: "#6b7280",
                    fontWeight: "600",
                    marginTop: "8px",
                    marginBottom: "4px",
                    lineHeight: "1.4",
                  }}
                >
                  {children}
                </h5>
              );
            },
            h6(props: any) {
              const { children, ...rest } = props;
              return (
                <h6
                  {...rest}
                  style={{
                    fontFamily: "Rubik Regular",
                    fontSize: "4px", // H6 = 4px (8-4)
                    color: "#9ca3af",
                    fontWeight: "600",
                    marginTop: "6px",
                    marginBottom: "4px",
                    lineHeight: "1.4",
                  }}
                >
                  {children}
                </h6>
              );
            },
            // Enhanced paragraph styling
            p(props: any) {
              const { children, ...rest } = props;
              return (
                <p
                  {...rest}
                  style={{
                    fontFamily: "Rubik Regular",
                    fontSize: (typography?.size || 14) + "px",
                    color: "#374151",
                    lineHeight: "1.6",
                    marginBottom: "8px",
                    textAlign: "left",
                  }}
                >
                  {children}
                </p>
              );
            },
            // Enhanced links with hover effects
            a(props: any) {
              const { children, href, ...rest } = props;
              return (
                <a
                  {...rest}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    fontFamily: "Rubik Regular",
                    fontSize: (typography?.size || 14) + "px",
                    color: "#3e9adc",
                    textDecoration: "underline",
                    cursor: "pointer",
                    transition: "color 0.2s ease-in-out",
                    display: "block",
                  }}
                >
                  {children}
                </a>
              );
            },
            // Enhanced lists with better spacing
            ul(props: any) {
              const { children, className, ...rest } = props;
              const match = /task-list/.exec(className || "");
              return match ? null : (
                <ul
                  {...rest}
                  className={className}
                  style={{
                    fontFamily: "Rubik Regular",
                    fontSize: (typography?.size || 14) + "px",
                    color: "#374151",
                    listStyleType: "disc",
                    paddingLeft: "20px",
                    marginBottom: "8px",
                    lineHeight: "1.6",
                  }}
                >
                  {children}
                </ul>
              );
            },
            ol(props: any) {
              const { children, className, node, ...rest } = props;
              if (className && className.includes("task-list")) {
                return null;
              }
              return (
                <ol
                  {...rest}
                  className={className}
                  style={{
                    fontFamily: "Rubik Regular",
                    fontSize: (typography?.size || 14) + "px",
                    color: "#374151",
                    listStyleType: "decimal",
                    paddingLeft: "20px",
                    marginBottom: "8px",
                    lineHeight: "1.6",
                  }}
                >
                  {children}
                </ol>
              );
            },
            li(props: any) {
              const { children, className, node, ...rest } = props;
              const childrenArray = React.Children.toArray(children);
              const processedChildren = childrenArray.map(
                (child: any, index: number) => {
                  if (typeof child === "string") {
                    return child
                      .split("\n\n")
                      .reduce((acc: any[], part: string, idx: number) => {
                        if (idx > 0)
                          acc.push(<br key={`br-double-${index}-${idx}`} />);
                        const subParts = part.split("\n");
                        subParts.forEach((subPart: string, subIdx: number) => {
                          if (subIdx > 0)
                            acc.push(
                              <br
                                key={`br-single-${index}-${idx}-${subIdx}`}
                              />,
                            );
                          if (subPart) acc.push(subPart);
                        });
                        return acc;
                      }, []);
                  }
                  return child;
                },
              );

              const match = /task-list-item/.exec(className || "");
              return match ? null : (
                <li
                  {...rest}
                  className={className}
                  style={{
                    fontFamily: "Rubik Regular",
                    fontSize: (typography?.size || 14) + "px",
                    color: "#374151",
                    marginBottom: "4px",
                    lineHeight: "1.6",
                  }}
                >
                  {processedChildren}
                </li>
              );
            },
            // Enhanced blockquotes
            blockquote(props: any) {
              const { children, ...rest } = props;
              return (
                <blockquote
                  {...rest}
                  style={{
                    fontFamily: "Rubik Regular",
                    fontSize: (typography?.size || 14) + "px",
                    color: "#6b7280",
                    borderLeft: "4px solid #3e9adc",
                    paddingLeft: "16px",
                    paddingTop: "4px",
                    paddingBottom: "4px",
                    margin: "10px 0",
                    fontStyle: "italic",
                    backgroundColor: "#eaf4fb",
                    borderRadius: "0 6px 6px 0",
                  }}
                >
                  {children}
                </blockquote>
              );
            },
            // Enhanced code blocks
            pre(props: any) {
              const { children, ...rest } = props;
              return (
                <pre
                  {...rest}
                  style={{
                    fontFamily:
                      "'SF Mono', 'Monaco', 'Inconsolata', 'Roboto Mono', monospace",
                    fontSize: (typography?.size || 14) - 1 + "px",
                    color: "#1f2937",
                    backgroundColor: "#f8fafc",
                    padding: "12px",
                    borderRadius: "6px",
                    overflowX: "auto",
                    whiteSpace: "pre-wrap",
                    margin: "8px 0",
                    border: "1px solid #e5e7eb",
                    boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
                  }}
                >
                  {children}
                </pre>
              );
            },
            // Inline code with better styling
            code(props: any) {
              const { children, className, ...rest } = props;
              // Check if this is a code block (has language class) vs inline code
              const isCodeBlock =
                className && className.startsWith("language-");

              if (isCodeBlock) {
                // This is handled by pre > code, so just return the children
                return <code {...rest}>{children}</code>;
              }

              // Inline code styling
              return (
                <code
                  {...rest}
                  style={{
                    fontFamily:
                      "'SF Mono', 'Monaco', 'Inconsolata', 'Roboto Mono', monospace",
                    fontSize: (typography?.size || 14) - 1 + "px",
                    color: "#dc2626",
                    backgroundColor: "#fef2f2",
                    padding: "2px 6px",
                    borderRadius: "4px",
                    border: "1px solid #fecaca",
                  }}
                >
                  {children}
                </code>
              );
            },
            // Enhanced tables
            table(props: any) {
              const { children, ...rest } = props;
              return (
                <div className="table-container-cbb my-4 w-full overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
                  <table
                    {...rest}
                    className="min-w-full border-collapse bg-white"
                    style={{
                      fontFamily: "Rubik Regular",
                      fontSize: (typography?.size || 14) + "px",
                      color: "#374151",
                      width: "100%",
                      display: "table",
                      tableLayout: "auto",
                    }}
                  >
                    {children}
                  </table>
                </div>
              );
            },
            thead(props: any) {
              const { children, ...rest } = props;
              return (
                <thead
                  {...rest}
                  style={{
                    backgroundColor: "#f9fafb",
                    color: "#374151",
                    display: "table-header-group",
                  }}
                >
                  {children}
                </thead>
              );
            },
            th(props: any) {
              const { children, ...rest } = props;
              return (
                <th
                  {...rest}
                  style={{
                    fontFamily: "Rubik Regular",
                    fontSize: (typography?.size || 14) + "px",
                    color: "#1f2937",
                    fontWeight: "600",
                    backgroundColor: "#f9fafb",
                    padding: "12px 16px",
                    textAlign: "left",
                    borderBottom: "2px solid #e5e7eb",
                    borderRight: "1px solid #e5e7eb",
                    display: "table-cell",
                    verticalAlign: "middle",
                  }}
                >
                  {children}
                </th>
              );
            },
            td(props: any) {
              const { children, style, ...rest } = props;

              let parsedStyle: any = {};
              if (typeof style === "string") {
                const styleRules = style
                  .split(";")
                  .filter((rule) => rule.trim());
                styleRules.forEach((rule) => {
                  const [property, value] = rule
                    .split(":")
                    .map((s) => s.trim());
                  if (property && value) {
                    const camelProperty = property.replace(/-([a-z])/g, (g) =>
                      g[1].toUpperCase(),
                    );
                    parsedStyle[camelProperty] = value;
                  }
                });
              } else if (typeof style === "object") {
                parsedStyle = style;
              }

              const baseStyle: any = {
                fontFamily: "Rubik Regular",
                fontSize: (typography?.size || 14) + "px",
                color: "#374151",
                padding: "12px 16px",
                borderBottom: "1px solid #e5e7eb",
                borderRight: "1px solid #e5e7eb",
                display: "table-cell",
                verticalAlign: "top",
                lineHeight: "1.5",
              };

              if (!parsedStyle.backgroundColor && !parsedStyle.background) {
                baseStyle.backgroundColor = "#ffffff";
              }

              const finalStyle = { ...baseStyle, ...parsedStyle };

              return (
                <td {...rest} style={finalStyle}>
                  {children}
                </td>
              );
            },
            tbody(props: any) {
              const { children, ...rest } = props;
              return (
                <tbody
                  {...rest}
                  style={{
                    color: "#374151",
                    display: "table-row-group",
                  }}
                >
                  {children}
                </tbody>
              );
            },
            tr(props: any) {
              const { children, ...rest } = props;
              return (
                <tr
                  {...rest}
                  style={{
                    color: "#374151",
                    display: "table-row",
                    transition: "background-color 0.15s ease-in-out",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#f9fafb";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                  }}
                >
                  {children}
                </tr>
              );
            },
            // Enhanced horizontal rule
            hr(props: any) {
              return (
                <hr
                  {...props}
                  style={{
                    border: "none",
                    height: "2px",
                    background:
                      "linear-gradient(to right, #e5e7eb, #9ca3af, #e5e7eb)",
                    margin: "16px 0",
                    borderRadius: "1px",
                  }}
                />
              );
            },
            // Enhanced emphasis and strong
            strong(props: any) {
              const { children, ...rest } = props;
              return (
                <strong
                  {...rest}
                  style={{
                    fontFamily: "Rubik Regular",
                    fontSize: (typography?.size || 14) + "px",
                    color: "#1f2937",
                    fontWeight: "600",
                  }}
                >
                  {children}
                </strong>
              );
            },
            em(props: any) {
              const { children, ...rest } = props;
              return (
                <em
                  {...rest}
                  style={{
                    fontFamily: "Rubik Regular",
                    fontSize: (typography?.size || 14) + "px",
                    color: "#374151",
                    fontStyle: "italic",
                  }}
                >
                  {children}
                </em>
              );
            },
            // Keep all your existing specialized components with minor improvements
            sup(props: any) {
              const { children, ...rest } = props;
              return (
                <sup
                  {...rest}
                  style={{
                    fontFamily: "Rubik Regular",
                    fontSize: (typography?.size || 14) - 3 + "px",
                    color: "#6b7280",
                    verticalAlign: "super",
                    lineHeight: "0",
                  }}
                >
                  {children}
                </sup>
              );
            },
            sub(props: any) {
              const { children, ...rest } = props;
              return (
                <sub
                  {...rest}
                  style={{
                    fontFamily: "Rubik Regular",
                    fontSize: (typography?.size || 14) - 3 + "px",
                    color: "#6b7280",
                    verticalAlign: "sub",
                    lineHeight: "0",
                  }}
                >
                  {children}
                </sub>
              );
            },
            kbd(props: any) {
              const { children, ...rest } = props;
              return (
                <kbd
                  {...rest}
                  style={{
                    fontFamily: "'SF Mono', 'Monaco', monospace",
                    fontSize: (typography?.size || 14) - 1 + "px",
                    color: "#374151",
                    backgroundColor: "#f3f4f6",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    padding: "3px 8px",
                    boxShadow:
                      "0 2px 4px rgba(0,0,0,0.1), inset 0 -2px 0 rgba(0,0,0,0.1)",
                    display: "inline-block",
                  }}
                >
                  {children}
                </kbd>
              );
            },
            mark(props: any) {
              const { children, ...rest } = props;
              return (
                <mark
                  {...rest}
                  style={{
                    backgroundColor: "#fef3c7",
                    color: "#92400e",
                    padding: "2px 4px",
                    borderRadius: "3px",
                  }}
                >
                  {children}
                </mark>
              );
            },
            del(props: any) {
              const { children, ...rest } = props;
              return (
                <del
                  {...rest}
                  style={{
                    fontFamily:
                      typography?.font?.fontFamily || "Arial, sans-serif",
                    fontSize: (typography?.size || 14) + "px",
                    color: "#9ca3af",
                    textDecoration: "line-through",
                  }}
                >
                  {children}
                </del>
              );
            },
            // Keep all your other specialized components (var, samp, small, abbr, etc.)
            // with similar color/spacing improvements...
            var(props: any) {
              const { children, ...rest } = props;
              return (
                <var
                  {...rest}
                  style={{
                    fontFamily: "'SF Mono', 'Monaco', monospace",
                    fontSize: (typography?.size || 14) + "px",
                    color: "#dc2626",
                    fontStyle: "italic",
                    backgroundColor: "#fef2f2",
                    padding: "1px 4px",
                    borderRadius: "3px",
                  }}
                >
                  {children}
                </var>
              );
            },
            samp(props: any) {
              const { children, ...rest } = props;
              return (
                <samp
                  {...rest}
                  style={{
                    fontFamily: "'SF Mono', 'Monaco', monospace",
                    fontSize: (typography?.size || 14) + "px",
                    color: "#059669",
                    backgroundColor: "#ecfdf5",
                    padding: "1px 4px",
                    borderRadius: "3px",
                  }}
                >
                  {children}
                </samp>
              );
            },
            small(props: any) {
              const { children, ...rest } = props;
              return (
                <small
                  {...rest}
                  style={{
                    fontFamily: "Rubik Regular",
                    fontSize: (typography?.size || 14) - 2 + "px",
                    color: "#9ca3af",
                  }}
                >
                  {children}
                </small>
              );
            },
            // Details/Summary with better styling
            details(props: any) {
              const { children, ...rest } = props;
              return (
                <details
                  {...rest}
                  style={{
                    margin: "10px 0",
                    border: "1px solid #e5e7eb",
                    borderRadius: "6px",
                    padding: "0",
                    backgroundColor: "#ffffff",
                    boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
                  }}
                >
                  {children}
                </details>
              );
            },
            summary(props: any) {
              const { children, ...rest } = props;
              return (
                <summary
                  {...rest}
                  style={{
                    fontFamily: "Rubik Regular",
                    fontSize: (typography?.size || 14) + "px",
                    color: "#374151",
                    fontWeight: "500",
                    padding: "12px 16px",
                    backgroundColor: "#f9fafb",
                    cursor: "pointer",
                    borderRadius: "6px 6px 0 0",
                    transition: "background-color 0.15s ease-in-out",
                  }}
                >
                  {children}
                </summary>
              );
            },
            // Add remaining components with similar improvements...
            img(props: any) {
              const { src, alt, ...rest } = props;
              return (
                <img
                  {...rest}
                  src={src}
                  alt={alt}
                  style={{
                    maxWidth: "100%",
                    height: "auto",
                    display: "block",
                    margin: "10px 0",
                    borderRadius: "6px",
                    boxShadow: "0 2px 4px -1px rgba(0, 0, 0, 0.1)",
                  }}
                />
              );
            },
          }}
        >
          {formatLineBreaks(item)}
        </Markdown>
      );
    });
  }, [content, typography, chatConfig?.theme]);

  const LoadingSkeleton = () => (
    <SkeletonTheme baseColor="#f3f4f6" highlightColor="#e5e7eb">
      <div className="loading-skeleton-container p-5">
        {/* Content blocks skeleton */}
        {[...Array(6)].map((_, index) => (
          <div
            key={index}
            className="skeleton-content-block mb-8 rounded-lg border border-gray-100 bg-gray-50 p-4"
          >
            {/* Regular paragraph skeleton */}
            <div className="mb-4">
              <Skeleton height={16} width="95%" className="mb-3" />
              <Skeleton height={16} width="87%" className="mb-3" />
              <Skeleton height={16} width="78%" />
            </div>
            {/* Occasional list skeleton */}
            {index % 4 === 1 && (
              <div className="skeleton-list my-4">
                <Skeleton height={16} width="40%" className="mb-2" />
                <div className="ml-4">
                  <Skeleton height={14} width="85%" className="mb-2" />
                  <Skeleton height={14} width="78%" className="mb-2" />
                  <Skeleton height={14} width="82%" />
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </SkeletonTheme>
  );
  return (
    <div className="createbot-modal data-source-modal view-details-modal">
      <style>{`
        /* Content transition styles */
        .content-container {
          transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .content-entering {
          opacity: 0;
          transform: translateY(20px);
        }

        .content-entered {
          opacity: 1;
          transform: translateY(0);
        }

        .content-exiting {
          opacity: 0;
          transform: translateY(-10px);
        }

        /* Staggered animation for content items */
        .content-item {
          opacity: 0;
          transform: translateY(15px);
          animation: slideInUp 0.5s ease-out forwards;
        }

        .content-item:nth-child(1) { animation-delay: 0.1s; }
        .content-item:nth-child(2) { animation-delay: 0.15s; }
        .content-item:nth-child(3) { animation-delay: 0.2s; }
        .content-item:nth-child(4) { animation-delay: 0.25s; }
        .content-item:nth-child(5) { animation-delay: 0.3s; }
        .content-item:nth-child(n+6) { animation-delay: 0.35s; }

        @keyframes slideInUp {
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* Enhanced scrollbar */
        .grey-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: #c1c1c1 #f1f1f1;
        }

        .grey-scrollbar::-webkit-scrollbar {
          width: 8px;
        }

        .grey-scrollbar::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 4px;
        }

        .grey-scrollbar::-webkit-scrollbar-thumb {
          background: #c1c1c1;
          border-radius: 4px;
          transition: background 0.3s ease;
        }

        .grey-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #a1a1a1;
        }

        :global(.search-highlight) {
          background-color: #FFFF00 !important;
          color: #000000bd !important;
          padding: 0 2px !important;
        }
        :global(.current-match) {
          background-color: #FF6B35 !important;
          color: white !important;
        }

        /* Loading skeleton container styles */
        .loading-skeleton-container {
          animation: fadeIn 0.3s ease-in;
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        .react-loading-skeleton {
          line-height: 1;
        }
      `}</style>
      <div
        className="linkPopup-container link-modal-wrapper"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="close flex-cnt" onClick={onClose}>
          <svg
            width="10"
            height="10"
            viewBox="0 0 10 10"
            fill="#6D6D6D"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M10 1.00714L8.99286 0L5 3.99286L1.00714 0L0 1.00714L3.99286 5L0 8.99286L1.00714 10L5 6.00714L8.99286 10L10 8.99286L6.00714 5L10 1.00714Z"
              fill="#6D6D6D"
            />
          </svg>
        </div>
        <div className="linkPopup-header fx p-relative w-link-header gap-[20px]">
          <span
            className="fx trainedLinksUrl gap-8 overflow-hidden whitespace-nowrap"
            title={displayHeading}
            onMouseEnter={(e) => {
              const container = e.currentTarget as HTMLElement;
              const inner = container.querySelector(
                ".scrolling-text",
              ) as HTMLElement;
              if (inner) {
                const scrollDistance =
                  inner.scrollWidth - container.offsetWidth;
                if (scrollDistance > 0) {
                  const speed = 100;
                  const duration = scrollDistance / speed;
                  inner.style.transition = `transform ${duration}s linear`;
                  inner.style.transform = `translateX(-${scrollDistance}px)`;
                }
              }
            }}
            onMouseLeave={(e) => {
              const container = e.currentTarget as HTMLElement;
              const inner = container.querySelector(
                ".scrolling-text",
              ) as HTMLElement;
              if (inner) {
                inner.style.transition = "transform 0.3s ease-out";
                inner.style.transform = "translateX(0)";
              }
            }}
          >
            <div className="scrolling-text flex items-center gap-2 whitespace-nowrap">
              <h2>{displayHeading}</h2>
            </div>
          </span>
          <div ref={boxRef} className="fetchUrlSearchBox fx al-fx-cnt cp gap-6">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="17"
              height="16"
              viewBox="0 0 17 16"
              fill="none"
            >
              <path
                d="M16.2108 14.8619L12.2314 10.8825C13.3159 9.55625 13.849 7.86392 13.7207 6.15556C13.5923 4.44721 12.8122 2.85354 11.5418 1.7042C10.2714 0.554862 8.60783 -0.0622172 6.8952 -0.019396C5.18257 0.0234252 3.55191 0.72287 2.34051 1.93426C1.12912 3.14566 0.429675 4.77632 0.386854 6.48895C0.344033 8.20158 0.961112 9.86516 2.11045 11.1356C3.25979 12.406 4.85346 13.1861 6.56181 13.3144C8.27017 13.4428 9.9625 12.9096 11.2888 11.8252L15.2681 15.8045C15.3938 15.926 15.5622 15.9932 15.737 15.9916C15.9118 15.9901 16.079 15.92 16.2027 15.7964C16.3263 15.6728 16.3964 15.5056 16.3979 15.3308C16.3994 15.156 16.3322 14.9876 16.2108 14.8619ZM7.07277 11.9999C6.01794 11.9999 4.98679 11.6871 4.10973 11.101C3.23267 10.515 2.54908 9.68204 2.14541 8.7075C1.74175 7.73296 1.63613 6.6606 1.84192 5.62604C2.0477 4.59147 2.55566 3.64117 3.30154 2.89529C4.04742 2.14941 4.99772 1.64145 6.03229 1.43567C7.06685 1.22988 8.13921 1.3355 9.11375 1.73916C10.0883 2.14283 10.9212 2.82642 11.5073 3.70348C12.0933 4.58054 12.4061 5.61169 12.4061 6.66652C12.4045 8.08052 11.8421 9.43615 10.8423 10.436C9.8424 11.4359 8.48677 11.9983 7.07277 11.9999Z"
                fill="#666666"
              />
            </svg>

            <input
              type="text"
              placeholder="Search your data..."
              autoFocus
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            {totalMatches > 0 && (
              <span
                style={{
                  fontSize: "12px",
                  color: "#666",
                  minWidth: "50px",
                  textAlign: "right",
                }}
              >
                {currentMatchIndex}/{totalMatches}
              </span>
            )}
          </div>
        </div>

        <div className="linkPopup-body fx fx-d-col fx fx-d-col gap-10">
          <div className="fx al-cnt jstfy-sp-btw">
            <h5>Extracted data</h5>
            <div className="fx al-cnt gap-10">
              <span className="copy cursor-pointer" onClick={handleCopy}>
                <svg
                  width="27"
                  height="26"
                  viewBox="0 0 27 26"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect
                    x="0.40625"
                    width="26"
                    height="26"
                    rx="5"
                    fill="#444444"
                    fillOpacity="0.1"
                  />
                  <path
                    d="M8.90625 12.25C8.90625 10.1287 8.90625 9.06802 9.56526 8.40901C10.2243 7.75 11.2849 7.75 13.4062 7.75H15.6562C17.7776 7.75 18.8382 7.75 19.4972 8.40901C20.1563 9.06802 20.1562 10.1287 20.1562 12.25V16C20.1562 18.1213 20.1563 19.182 19.4972 19.841C18.8382 20.5 17.7776 20.5 15.6562 20.5H13.4062C11.2849 20.5 10.2243 20.5 9.56526 19.841C8.90625 19.182 8.90625 18.1213 8.90625 16V12.25Z"
                    stroke="#444444"
                    strokeWidth="1.5"
                  />
                  <path
                    opacity="0.4"
                    d="M8.65625 17.75C7.41361 17.75 6.40625 16.7426 6.40625 15.5V11C6.40625 8.17157 6.40625 6.75736 7.28493 5.87868C8.16361 5 9.57782 5 12.4062 5H15.4062C16.6489 5 17.6562 6.00736 17.6562 7.25"
                    stroke="#444444"
                    strokeWidth="1.5"
                  />
                </svg>
              </span>
            </div>
          </div>
          <div className="viewDetails-content">
            {isLoading ? (
              <div className="grey-scrollbar">
                <LoadingSkeleton />
              </div>
            ) : content.length === 0 ? (
              <div className="no-details flex flex-col items-center justify-center gap-[50px]">
                <img
                  src="https://dlvkyia8i4zmz.cloudfront.net/KaKDjdilS8iD0WxchO0F_no_chats_found.svg"
                  alt="No details found"
                  className="h-[300px]"
                />
                <h6 className="text-center text-[16px] font-medium leading-[19px] text-[#444]">
                  No result found
                </h6>
              </div>
            ) : (
              <div ref={contentRef} className="grey-scrollbar">
                {renderedContent}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewDetailsModal;
